import clsx from 'clsx';
import * as styles from '../styles/containers/SelectedNode.less';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import assign from 'lodash/assign';
import findIndex from 'lodash/findIndex';
import _ from 'lodash';
import { withTranslation } from "react-i18next";
import { TARGET_URL, QUESTIONS, PICTURES, THEMES, QRCODE_SCALE, TARGET_TYPES, MEASUREMENT_TYPES } from '../constants/config';
import Loader from '../components/common/Loader';
import Timestamps from '../components/common/Timestamps';
import ParentSelector from '../components/target/ParentSelector';
import PictureSelector from '../components/target/PictureSelector';


import TopicButtonsPanel from '../components/target/TopicButtonsPanel';
import ContactFormPanel from '../components/target/ContactFormPanel';
import AdditionalQuestionsPanel from '../components/target/AdditionalQuestionsPanel';
import ThankYouPanel from '../components/target/ThankYouPanel';
import UrlTogglePanel from '../components/target/UrlTogglePanel';
import QrCodePanel from '../components/target/QrCodePanel';
import SuperuserSettingsPanel from '../components/target/SuperuserSettingsPanel';
import TargetLinkingPanel from '../components/target/TargetLinkingPanel';
import QuestionPanel from '../components/target/QuestionPanel';
import TargetActionsPanel from '../components/target/TargetActionsPanel';
import TargetTitleFields from '../components/target/TargetTitleFields';
import SpecialSettingsPanel from '../components/target/SpecialSettingsPanel';
import { getTargetId } from '../utils/helpers';
import { TargetNode, TargetSummary, TargetExtra } from '../models/TargetNode';
import { TreeState } from '../reducers/tree';
import { useActions } from '../hooks/useActions';
// Side-effect stylesheet for rc-collapse. The container no longer renders a
// Collapse itself — the extracted panels do — but the CSS still has to be loaded
// once, so this import is NOT unused despite appearing so.
import '!style-loader!css-loader!rc-collapse/assets/index.css';
import SuperuserIsActive from './SuperuserIsActive';
// Per-field metadata for the 37 contact-form / topic-button checkboxes. This
// used to live as data-* attributes on the inputs and be read back off the DOM
// at save time (via _bindField/targetExtras). The inputs are controlled off
// extra.k now, so the table is the only place it needs to exist and the JSX
// keeps only data-redisid, for identity.
//
// ORDER MATTERS: it fixes the order of the rebuilt extra.k array, matching the
// old ref-callback registration order (which was DOM mount order).
//
// Every entry maps to extra.k — the old TARGET_PROPERTY_MAPPINGS table existed
// only to record that ('k' for all 37), so it is gone.
const CONTACT_FIELDS = [
  { name: 'topicButtonsShowTitle', redisid: 'topicButtonsTitle', redislabel: 'Topic Buttons Title' },
  { name: 'topicButtonsShowSubtitle', redisid: 'topicButtonsSubtitle', redislabel: 'Detail Buttons Subtitle' },
  { name: 'topicButtonsShow_1', redisid: 'topicButton_1', redislabel: 'Button 1' },
  { name: 'topicButtonsShow_2', redisid: 'topicButton_2', redislabel: 'Button 2' },
  { name: 'topicButtonsShow_3', redisid: 'topicButton_3', redislabel: 'Button 3' },
  { name: 'topicButtonsShow_4', redisid: 'topicButton_4', redislabel: 'Button 4' },
  { name: 'topicButtonsShow_5', redisid: 'topicButton_5', redislabel: 'Button 5' },
  { name: 'topicButtonsShow_6', redisid: 'topicButton_6', redislabel: 'Button 6' },
  { name: 'topicButtonsShow_7', redisid: 'topicButton_7', redislabel: 'Button 7' },
  { name: 'topicButtonsShow_8', redisid: 'topicButton_8', redislabel: 'Button 8' },
  { name: 'contactFormPleaseContactMe', redisid: 'contactme', redislabel: 'Contact me', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormShowName', redisid: 'name', redislabel: 'Name' },
  { name: 'contactFormShowCompany', redisid: 'company', redislabel: 'Company' },
  { name: 'contactFormShowEmail', redisid: 'email', redislabel: 'Email' },
  { name: 'contactFormShowPhone', redisid: 'phone', redislabel: 'Phone' },
  { name: 'contactFormShowRoomNumber', redisid: 'room', redislabel: 'Room' },
  { name: 'contactFormShowTeam', redisid: 'team', redislabel: 'Team' },
  { name: 'contactFormShowStreetAddress', redisid: 'streetAddress', redislabel: 'Street address' },
  { name: 'contactFormShowPostalCode', redisid: 'postalCode', redislabel: 'Postal code' },
  { name: 'contactFormShowCity', redisid: 'city', redislabel: 'City' },
  { name: 'contactFormShowCountry', redisid: 'country', redislabel: 'Country' },
  { name: 'contactFormShowAge', redisid: 'age', redislabel: 'Age' },
  { name: 'contactFormShowGender', redisid: 'gender', redislabel: 'Gender' },
  { name: 'contactFormCustom1', redisid: 'custom1', redislabel: 'Custom 1', format: 'checkbox' },
  { name: 'contactFormCustom2', redisid: 'custom2', redislabel: 'Custom 2', format: 'checkbox' },
  { name: 'contactFormCustom3', redisid: 'custom3', redislabel: 'Custom 3', format: 'checkbox' },
  { name: 'contactFormCustom4', redisid: 'custom4', redislabel: 'Custom 4' },
  { name: 'contactFormCustom5', redisid: 'custom5', redislabel: 'Custom 5' },
  { name: 'contactFormCustom6', redisid: 'custom6', redislabel: 'Custom 6' },
  { name: 'contactFormOkSendMarketingSMS', redisid: 'nomarketingsms', redislabel: 'No marketing SMS', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormOkSendMarketingEmail', redisid: 'nomarketingemail', redislabel: 'No marketing emails', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormOkSendMarketingCalls', redisid: 'nomarketingcall', redislabel: 'No marketing calls', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormNoMarketingMessages', redisid: 'nomarketingmessages', redislabel: 'No marketing messages', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormDoNotShowResults', redisid: 'dontshowresults', redislabel: 'Do not show results', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormPublicMonitor', redisid: 'publicmonitor', redislabel: 'Enable public monitor', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormShowDescOnTarget', redisid: 'showdescription', redislabel: 'Show description text on target', redisvalue: 'false', format: 'checkbox' },
  { name: 'contactFormEnableImageUpload', redisid: 'imageupload', redislabel: 'Enable image upload', redisvalue: 'false', format: 'checkbox' },
];

const CONTACT_FIELDS_BY_ID = keyBy(CONTACT_FIELDS, 'redisid');

// redisid -> the label map of a field that was unticked, so re-ticking restores
// its localizations.
const CACHED_LOCALIZATIONS: Record<string, Record<string, string>> = {};

// exitLink: c99

//-------------------------------------------------------------
// Derive the editor's field defaults from the selected node.
//
// This is the body of the old `_bindEnable` ref callback, lifted out verbatim
// into a pure function: given the `selectedNode`, it returns a plain object of
// the `_…` field values the editor seeds itself with. No `this`, no mutation of
// the node — same reads, same defaults, same order as before. Phase 5 has been
// emptying it: most fields are now read from the store at render, so what is
// left here is only the seeds that feed useState.
//-------------------------------------------------------------
// What deriveNodeState still returns after Phase 5: only the fields that feed
// seed-time useState. Everything else is read from the store at render.
interface NodeSeed {
  _tags: string[] | string;
  _qrcodeTitle: string;
  _qrcodeURL: string;
  _qrcodeId: number;
  _qrcodeScale: number;
  _iconPrefix: string;
  // Stored values can be strings: min/max come back from number inputs as text.
  _minValue: number | string;
  _maxValue: number | string;
  _votingScale: number | string;
}

// Most handlers here take a change event from an <input>; the localization ones
// take a <textarea> event (the panels' LocalizationChangeHandler). tsc
// distinguishes them, which is how the mismatch found in Phase 7.4 surfaced.
type InputEvent = React.ChangeEvent<HTMLInputElement>;
type TextAreaEvent = React.ChangeEvent<HTMLTextAreaElement>;

function deriveNodeState(node: TargetNode): NodeSeed {
  // node is a plain JS object (Phase 3 boundary), not an Immutable Map.
  const s: Partial<NodeSeed> = {};

  const tagList = node.tags;
  if (typeof tagList === 'undefined' || tagList.length === 0) {
    s._tags = '';
  } else {
    s._tags = tagList; // already a plain array
  }

  // Tags
  var newTags = node.extra?.c?.c84;
  if (Array.isArray(newTags)) {
    s._tags = newTags;
  }

  // The theme id itself is a derived const in the component now; it survives
  // here only because the four scalars below key their defaults off it.
  const themeId = node.extra?.c?.c42 ?? 2;  // 2 = Feelings theme
  const themeIndex = findIndex(THEMES, { value: themeId });

  // Generic icon path
  s._iconPrefix = node.extra?.c?.c5;
  if (!s._iconPrefix || typeof s._iconPrefix === 'undefined') {
    s._iconPrefix = THEMES[themeIndex].iconPrefix;
  }

  // Min value
  s._minValue = node.extra?.c?.minValue;
  if (typeof s._minValue === 'undefined') {
    s._minValue = THEMES[themeIndex].minValue;
  }

  // Max value
  s._maxValue = node.extra?.c?.maxValue;
  if (typeof s._maxValue === 'undefined') {
    s._maxValue = THEMES[themeIndex].maxValue;
  }

  // // Generic voting scale
  s._votingScale = node.extra?.c?.c6;
  if (!s._votingScale || typeof s._votingScale === 'undefined') {
    s._votingScale = THEMES[themeIndex].votingScale;
  }

  // Target URL with theme type. A local now, not a bag slot — the component
  // derives its own `typePath`; this copy only seeds the QR URL default below.
  const typePath = TARGET_URL + '/' + TARGET_TYPES[node.extra?.c?.c62 || 0].path + '/' + node._id;

  // QR-code title
  s._qrcodeTitle = node.extra?.c?.c71;
  if (!s._qrcodeTitle || typeof s._qrcodeTitle === 'undefined') {
    s._qrcodeTitle = `${node.name}`;
  }

  // QR-code URL
  s._qrcodeURL = node.extra?.c?.c72;
  if (typeof s._qrcodeURL === 'string') {
    s._qrcodeURL = s._qrcodeURL.replace(/^https:\/\//, '');
  }
  if (!s._qrcodeURL || typeof s._qrcodeURL === 'undefined') {
    s._qrcodeURL = typePath;
  }

  // QR-code id. Guard on absence, not falsiness — XXL's id is 0, so the old
  // `if (!s._qrcodeId)` re-seeded a stored XXL back to 2 ('L') on every load.
  s._qrcodeId = node.extra?.c?.c74;
  if (typeof s._qrcodeId === 'undefined' || s._qrcodeId === null) {
    s._qrcodeId = 2; // 'L'
  }

  // QR-code scale. Same guard for consistency; no valid scale is falsy (XXL's
  // is 0.5), so this only changes behaviour for values that were never valid.
  s._qrcodeScale = node.extra?.c?.c73;
  if (typeof s._qrcodeScale === 'undefined' || s._qrcodeScale === null) {
    s._qrcodeScale = QRCODE_SCALE[s._qrcodeId].scale;
  }

  return s as NodeSeed;
}

const SelectedNode = (props: any) => {
  const render = () => {
    if (isTargetLoading) {
      return (
        <Loader style={{ padding: "2em", textAlign: "center" }} className={props.className} />
      );
    }
    const { t } = props;  // [t, i18n]
    if (!selectedNode._id) {
      return null;
    }
    // The five editable strings live in the English question array. Indices are
    // fixed: 0 is the question itself (rendered by <QuestionPanel>), then the
    // positive/negative follow-ups, the contact-form title, the thank-you text,
    // and the neutral follow-up. Note 5 is neutral, NOT 3 — easy to misread.
    // The try/catch guards a node whose `question` has no 'en' key at all.
    const questionText = (index: number) => {
      try {
        return selectedNode.question!['en'][index] || '';
      } catch (e) {
        return '';
      }
    };
    const plus = questionText(1);
    const minus = questionText(2);
    const contactTitle = questionText(3);
    const thankyouText = questionText(4);
    const neutral = questionText(5);



    return (
      <div className={clsx(styles.node, props.className)} >
        {isDeleted && <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.5)',
          zIndex: 10,
          pointerEvents: 'auto'
        }} />}
        <div className={styles.name}>

          <div  className={clsx(styles['target--title-button-group'], 'target--title-button-group')}>

            <TargetTitleFields
              targetId={selectedNode._id}
              onCopyId={targetIdToClipboard}
              customerName={targetCustomerName}
              customerId={customerId}
              customers={targetCustomers}
              onChangeCustomer={onChangeTargetCustomerName}
              name={selectedNode.name || ''}
              onChangeName={onChangeName}
              subtitle={selectedNode.extra?.c?.c82 || ''}
              onChangeSubtitle={onChangeSubtitle}
              tags={tagsText}
              onChangeTags={onChangeTags}
              styles={styles} />
            <div  className={clsx(styles['target--title-break'], 'target--title-break')}></div>
            <TargetActionsPanel
              targetName={selectedNode.name ?? ''}
              isTargetSaving={isTargetSaving}
              isTargetDeleting={isTargetDeleting}
              onSave={onSaveTarget}
              isEnabled={isEnabled}
              onToggleEnabled={onChangeEnabled}
              targetType={targetType}
              onChangeType={onChangeType}
              typePath={typePath}
              showDeleteDialog={showDeleteDialog}
              onDeleteClick={onHandleDeleteClick}
              onConfirmDelete={onHandleConfirm}
              onCancelDelete={onHandleCancel}
              showDeleteFeedbackDialog={showDeleteFeedbackDialog}
              onDeleteFeedbackClick={onHandleDeleteFeedbackClick}
              onConfirmDeleteFeedback={onHandleConfirmDeleteFeedback}
              onCancelDeleteFeedback={onHandleCancelDeleteFeedback}
              styles={styles} />
          </div>
          <div style={{ clear: "both" }}></div>
        </div>
        <div style={{ marginTop: "0.0em" }}><label >{t('target.editor.label.description')}</label></div>
        <input type="text"
          className="pure-u-24-24"
          value={selectedNode.extra?.c?.c83 || ''}
          onChange={onChangeDescription}
          title="This description is for documenting purposes only. It's not used in any other place." />
        <ParentSelector
          className={styles['parent--selector']}
          node={selectedNode}
          targets={targets}
          onChangeParent={onChangeParent} />
        <div className={styles['target--wrapper']}>

          <TargetLinkingPanel
            node={selectedNode}
            targets={targets}
            enabled={linkingEnabled}
            onToggleEnabled={onChangeEnableLinking}
            getback={getback}
            onChangeGetback={onChangeGetback}
            baseTarget={baseTarget}
            onChangeBaseTarget={onChangeSetTarget}
            prevTargetId={prevTargetId}
            nextTargetId={nextTargetId}
            onGoToPrev={goToPrevTarget}
            onGoToNext={goToNextTarget}
            onSelectPrev={selectPrevTarget}
            onSelectNext={onChangeNext}
            isTargetSaving={isTargetSaving}
            jumpButtonClassName={styles['jump--button']}
            selectorClassName={styles['target--selector']} />
          <PictureSelector
            className={styles['picture--selector']}
            node={selectedNode}
            onChangePicture={onChangePicture}
            onFileUpload={onFileUpload} />
          <QuestionPanel
            node={selectedNode}
            onChangeQuestion={onChangeQuestion}
            questionLocalizationChanged={questionLocalizationChanged}
            skipButton={skipButton}
            onToggleSkipButton={onChangeSkipButton}
            selectorClassName={styles['question--selector']} />
          <TopicButtonsPanel
            node={selectedNode}
            enabled={showDetailButtons}
            extraKIds={extraKIds}
            onToggleEnabled={onChangeShowTopicButton}
            onChangeExtra={onChangeExtra}
            localizationChanged={contactExtraLocalizationChanged} />
          <AdditionalQuestionsPanel
            node={selectedNode}
            enabled={optionalQuestions}
            topicButtonsEnabled={showDetailButtons}
            onToggleEnabled={onChangeOptionalQuestions}
            positive={{ value: plus, onChange: feedbackPlusChanged, onLocalizationChange: feedbackPlusLocalizationChanged }}
            negative={{ value: minus, onChange: feedbackMinusChanged, onLocalizationChange: feedbackMinusLocalizationChanged }}
            neutral={{ value: neutral, onChange: feedbackNeutralChanged, onLocalizationChange: feedbackNeutralLocalizationChanged }} />
          <ContactFormPanel
            node={selectedNode}
            enabled={optionalFields}
            extraKIds={extraKIds}
            onToggleEnabled={onChangeOptionalFields}
            onChangeExtra={onChangeExtra}
            localizationChanged={contactExtraLocalizationChanged}
            contactTitle={contactTitle}
            onChangeContactTitle={contactTitleChanged}
            contactTitleLocalizationChanged={contactTitleLocalizationChanged} />
          <ThankYouPanel
            node={selectedNode}
            enabled={optionalThanks}
            onToggleEnabled={onChangeOptionalThanks}
            thankyouText={thankyouText}
            onChangeThankyou={thankyouChanged}
            thankyouLocalizationChanged={thankyouLocalizationChanged}
            extraKIds={extraKIds}
            onChangeExtra={onChangeExtra} />
          <fieldset>
            <legend>{t('target.editor.label.otherSettings')}</legend>
            <SuperuserIsActive>
              <SuperuserSettingsPanel
                containerClassName={styles['other-superuser-settings']}
                themeSelectorClassName={styles['theme--selector']}
                iconsOrderButtonClassName={styles['enable-button']}
                viewLinkClassName={styles['target--link']}
                viewHref={`${TARGET_URL}/generic/${selectedNode._id}`}
                showViewLink={!isTargetSaving}
                themeSettings={{
                  node: selectedNode,
                  themeId,
                  onChangeTheme,
                  measurementTypeId,
                  onChangeMeasurementType,
                  categoryId,
                  onChangeCategory,
                  iconPrefix,
                  onChangeIconPrefix,
                  minValue,
                  onChangeMinValue,
                  maxValue,
                  onChangeMaxValue,
                  votingScale,
                  iconsOrder,
                  onChangeIconsOrder,
                }}
                campaignEnabled={!!selectedNode.extra?.c?.c21}
                onChangeCampaign={onChangeCampain}
                campaignTtl={String(selectedNode.extra?.c?.c23 ?? '')}
                onChangeCampaignTtl={onChangeCampainTtl}
                campaignKey={selectedNode.extra?.c?.c22 || ''}
                onChangeCampaignKey={onChangeCampainName}
                noVoting={!!selectedNode.extra?.c?.c46}
                whistleblower={!!selectedNode.extra?.c?.c63}
                dropZeroNps={!!selectedNode.extra?.c?.c86}
                dropNegativeNps={!!selectedNode.extra?.c?.c87}
                noAutosubmit={!!selectedNode.extra?.c?.c88}
                showNpsFooter={!!selectedNode.extra?.c?.c79}
                locationEnabled={!!selectedNode.extra?.c?.gps}
                onToggle={onToggleSetting} />
            </SuperuserIsActive>

            <div style={{ height: "0.4em" }}></div>
            <div>
              <label>{t('target.editor.label.imageButton')}</label>
              <input type="checkbox" data-redisid="imagebutton"
                title="Show image button"
                checked={!!selectedNode.extra?.c?.c47}
                onChange={onChangeImageButton}
              />
            </div>
            <div style={{ height: "0.4em" }}></div>
            <div>
              <label>{t('target.editor.label.languageMenu')}</label>
              <input type="checkbox" data-redisid="languagemenu"
                title="Show language menu"
                checked={!!selectedNode.extra?.c?.c9}
                onChange={onChangeShowLanguageMenu}
              />
            </div>
            <div style={{ height: "0.4em" }}></div>
            <label>{t('target.editor.label.endPage')}</label>
            <input type="text" data-redisid="landingpage"
              value={selectedNode.extra?.c?.c99 || ''}
              onChange={onChangeLandingPage} />
            <div style={{ height: "0.2em" }}></div>

            <UrlTogglePanel
              legendKey="target.editor.label.legalNotice"
              urlLabelKey="target.editor.label.legalNoticeURL"
              toggleRedisId="showlegalnotice"
              urlRedisId="legalurl"
              toggleTitle="Enable Legal Notice"
              enabled={legalNotice}
              onToggleEnabled={onChangeLegalNotice}
              url={selectedNode.extra?.c?.c81 || ''}
              onChangeUrl={onChangeLegalURL} />
            <div style={{ height: "0.2em" }}></div>
            <UrlTogglePanel
              legendKey="target.editor.label.lotteryRules"
              urlLabelKey="target.editor.label.lotteryRulesURL"
              toggleRedisId="showlotteryrules"
              urlRedisId="lotteryurl"
              toggleTitle="Enable Lottery Rules"
              enabled={lotteryRules}
              onToggleEnabled={onChangeLotteryRules}
              url={selectedNode.extra?.c?.c61 || ''}
              onChangeUrl={onChangeLotteryURL} />
            <div style={{ height: "0.2em" }}></div>
            <SpecialSettingsPanel
              linkingTarget={selectedNode.extra?.c?.c70 || ''}
              onChangeLinkingTarget={onChangeLinkingTarget}
              css={selectedNode.extra?.c?.css || ''}
              onChangeCss={onChangeCSS} />

            <QrCodePanel
              node={selectedNode}
              title={qrcodeTitle}
              onChangeTitle={onChangeQRcodeTitle}
              url={qrcodeURL}
              onChangeUrl={onChangeQRcodeURL}
              scale={qrcodeScale}
              onChangeScale={onChangeQRcode}
              selectorClassName={styles['qrcode--selector']}
              buttonContainerClassName={styles['target--button-container']}
              linkClassName={styles['target--link']} />

          </fieldset>
        </div>
        {isTargetSaving && <Loader />}
        {!isTargetSaving &&
          <button className="button success" onClick={() => onSaveTarget()}>
            <i className="fa fa-floppy-o" aria-hidden="true"></i> {t('button.save')}
          </button>}

        {selectedNode.parent && !isTargetSaving &&
          <button className="button success" onClick={onCloneTarget}>
            <i className="fa fa-clone" aria-hidden="true"></i> {t('button.clone')}
          </button>}
        <Timestamps
          createdAt={selectedNode.createdAt}
          updatedAt={selectedNode.updatedAt}
        />
      </div>
    );
  }
  //--------------------------------------------------------------------
  // Constructor
  //--------------------------------------------------------------------
  const { t } = props;
  const params = props.params;
  const actions = useActions();

  // Phase 4: s.tree is now plain (reducer de-Immutabled); selectedNode is a plain
  // object read directly. The old memoized .toJS() boundary is gone — the whole
  // read-side already consumes plain objects.
  // NOTE: two selectors reading s.dashboard.targetCustomerName / .targetCustomers
  // used to sit here. No reducer or action ever defines those keys, so both were
  // permanently undefined and everything read the self bag instead. Dropped.
  const selectedNode: TargetNode = useSelector((s: { tree: TreeState }) => s.tree.selectedNode);
  const targets: TargetSummary[] = useSelector((s: { tree: TreeState }) => s.tree.targets);
  const isTargetLoading = useSelector((s: { tree: TreeState }) => s.tree.isTargetLoading);
  const isTargetSaving = useSelector((s: { tree: TreeState }) => s.tree.isTargetSaving);
  const isTargetDeleting = useSelector((s: { tree: TreeState }) => s.tree.isTargetDeleting);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteFeedbackDialog, setShowDeleteFeedbackDialog] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // The `self` instance-bag is GONE (Phase 5 complete). It emulated the old class
  // `this`: a mutable object holding the `_…` field values and DOM-element refs,
  // with `_bindField` collecting inputs into `targetExtras` for imperative reads.
  // Every field now lives in the store or in React state, and extra.k drives the
  // contact/topic checkboxes directly.

  // Seed the value-field defaults when a different TARGET loads — the hook
  // equivalent of the old _bindEnable remount seeding (which re-ran only when the
  // loading toggle remounted the editor, i.e. on a new target). Keyed on the node
  // _id, NOT identity: editNode replaces selectedNode on every edit but keeps the
  // same _id, and re-seeding then would clobber handler-set field values. Doing
  // it during render (not an effect) means the editor never flashes stale values.
  // The four QR-code fields are real React state rather than `self` slots: they
  // have derived defaults (title falls back to the node name, URL to the type
  // path, scale to the id's entry in QRCODE_SCALE) that must be computed once per
  // TARGET, not per render — otherwise clearing a field would immediately snap
  // back to its default. deriveNodeState still computes the seed; we just read it
  // out into state at the same _id-keyed moment. Setting state during render is
  // the supported "adjust state when props change" pattern and converges here,
  // because seededIdRef is updated in the same pass.
  const [qrcodeTitle, setQrcodeTitle] = useState('');
  const [qrcodeURL, setQrcodeURL] = useState('');
  const [qrcodeId, setQrcodeId] = useState(2);
  const [qrcodeScale, setQrcodeScale] = useState(QRCODE_SCALE[2].scale);

  // Same reasoning for the theme-derived scalars: their defaults come from the
  // selected THEME (iconPrefix/minValue/maxValue/votingScale), and picking a new
  // theme resets all four. Seed-time state, not a render-time fallback.
  const [iconPrefix, setIconPrefix] = useState('');
  const [minValue, setMinValue] = useState<number | string>(0);
  const [maxValue, setMaxValue] = useState<number | string>(0);
  const [votingScale, setVotingScale] = useState<number | string>(5);

  // Tags. The handler normalises input into an array of quote-wrapped strings,
  // so binding the input straight to c84 would re-inject quotes on every
  // keystroke and fight the typing. The raw text lives in state (seeded per
  // target) and the normalised array goes to the store. Declared here with the
  // other seeded state because the seed block below calls its setter.
  const [tagsText, setTagsText] = useState('');

  // An explicit previous-target pick from the dropdown. Declared here with the
  // other seeded state because the seed block below resets it per target.
  const [pickedPrevTargetId, setPickedPrevTargetId] = useState<string | undefined>(undefined);

  const seededIdRef = useRef<string | false | undefined>(false);
  const nodeId = selectedNode && selectedNode._id;
  if (seededIdRef.current !== nodeId) {
    const seed = deriveNodeState(selectedNode);
    Object.assign(self, seed);
    seededIdRef.current = nodeId;
    setTagsText(Array.isArray(seed._tags) ? seed._tags.join(',') : (seed._tags || ''));
    setPickedPrevTargetId(undefined);
    setQrcodeTitle(seed._qrcodeTitle);
    setQrcodeURL(seed._qrcodeURL);
    setQrcodeId(seed._qrcodeId);
    setQrcodeScale(seed._qrcodeScale);
    setIconPrefix(seed._iconPrefix);
    setMinValue(seed._minValue);
    setMaxValue(seed._maxValue);
    setVotingScale(seed._votingScale);
  }

  // The contact-form / topic-button panel toggles. Derived rather than seeded:
  // they're plain reads of the store, but each carries the default its
  // deriveNodeState block used to apply — c45/c49/c50 default ON, c48/c78 OFF —
  // and they're read from a dozen places (checked, the hidden-gates, and both
  // write-out maps), so the default lives here once instead of at every site.
  // The superuser customer picker. The list itself is fetched, so it stays
  // component state; the selected name/id are read back off the node. Faithful
  // to the old seed, including its quirk: the id is taken from `customeId`
  // (sic — that typo is the stored field) but a name match against the fetched
  // list wins, and an empty name forces the id to 0.
  const [targetCustomers, setTargetCustomers] = useState<Array<{ label: string; value: number }>>([]);
  const storedCustomerName = selectedNode.customerName;
  const hasCustomerName = typeof storedCustomerName === 'string' && storedCustomerName.length > 0;
  const targetCustomerName = hasCustomerName ? storedCustomerName : '';
  let customerId = 0;
  if (hasCustomerName) {
    customerId = typeof selectedNode.customeId === 'number' ? selectedNode.customeId : 0;
    const match = targetCustomers.filter((item: { label: string }) => item.label === targetCustomerName);
    if (match.length > 0) {
      customerId = match[0].value;
    }
  }

  // The contact-form / topic-button checkboxes are "is my data-redisid present
  // in extra.k". onChangeExtra already maintains that array as a presence toggle
  // (absent → push, present → filter out), so the store is the whole truth and
  // the boxes can simply read it — replacing an effect that used to set
  // `node.checked = true` directly on the DOM.
  const extraKIds = new Set((selectedNode.extra?.k || []).map((field: any) => field && field.id));

  // Which previous target the jump button points at. A "previous" target is one
  // whose c70 link points at this node — the same rule PreviousSelector uses to
  // build its options — so when there is exactly one candidate it is derived,
  // matching the selector's own auto-select. An explicit pick from the dropdown
  // overrides it, and that pick is reset per target in the seed block above
  // (otherwise it leaks: the button stayed enabled on every later target once
  // any previous had been chosen).
  const prevCandidates = (targets || []).filter(
    (target: any) => target && getTargetId(target.c70) == selectedNode._id
  );
  const derivedPrevTargetId = prevCandidates.length === 1 ? prevCandidates[0]._id : undefined;
  const prevTargetId = pickedPrevTargetId ?? derivedPrevTargetId;

  // The linking group. c67 is the only stored value here — baseTarget is just
  // "is c67 set", and nextTargetId is parsed back out of the c70 link, so both
  // are derived rather than tracked. linkingEnabled keeps the seed's second
  // rule: a target that has a parseable c70 link counts as linked even if c68
  // was never written.
  const setTargetId = selectedNode.extra?.c?.c67 || '';
  const baseTarget = !!setTargetId;
  const getback = !!selectedNode.extra?.c?.c66;
  const nextTargetId = getTargetId(selectedNode.extra?.c?.c70 ?? '') || '';
  const linkingEnabled = !!selectedNode.extra?.c?.c68 || !!nextTargetId;

  // Enabled defaults to true when unset. typeof rather than ?? so an explicitly
  // stored null keeps the seed's old (falsy → disabled) reading.
  const storedEnabled = selectedNode.extra?.c?.enabled;
  const isEnabled = typeof storedEnabled === 'undefined' ? true : storedEnabled;

  // Target type, and the public URL derived from it. typePath was a bag slot
  // written in two places; it is a pure function of the type and the node id,
  // so it is just computed here.
  const targetType = selectedNode.extra?.c?.c62 || 0;
  const typePath = TARGET_URL + '/' + TARGET_TYPES[targetType].path + '/' + selectedNode._id;

  // The selector fields. Derived rather than seeded like the QR/theme scalars:
  // these are dropdowns and a toggle button, so there is no "user cleared it"
  // state a render-time fallback could clobber. Each carries its old seed
  // default. measurementTypeId's default is the theme id, but only when the
  // theme has a matching MEASUREMENT_TYPES row — and only when nothing is
  // stored, so an explicitly stored null stays null (hence the typeof test
  // rather than ??).
  const themeId = selectedNode.extra?.c?.c42 ?? 2;  // 2 = Feelings theme
  const storedMeasurementTypeId = selectedNode.extra?.c?.measurementTypeId;
  const measurementTypeId =
    typeof storedMeasurementTypeId === 'undefined' && MEASUREMENT_TYPES.some(x => x.value === themeId)
      ? themeId
      : storedMeasurementTypeId;
  const categoryId = selectedNode.extra?.c?.categoryId || 0;
  const iconsOrder = selectedNode.extra?.c?.c7 || '>';
  const timeoutLang = selectedNode.extra?.c?.c8 || 'fi';

  // Legal notice defaults ON when unset; lottery rules OFF. Both gate a single
  // URL field, so they share <UrlTogglePanel> and the default is applied here
  // rather than inside it.
  const legalNotice = !!(selectedNode.extra?.c?.c80 ?? true);
  const lotteryRules = !!(selectedNode.extra?.c?.c60 ?? false);

  const skipButton = !!(selectedNode.extra?.c?.c48 ?? false);
  const showDetailButtons = !!(selectedNode.extra?.c?.c78 ?? false);
  const optionalQuestions = !!(selectedNode.extra?.c?.c45 ?? true);
  const optionalFields = !!(selectedNode.extra?.c?.c50 ?? true);
  const optionalThanks = !!(selectedNode.extra?.c?.c49 ?? true);

  // componentDidUpdate compared prevProps node id; track it across renders.
  const prevNodeIdRef = useRef(selectedNode && selectedNode._id);

  // Fetch the customer list once (was in the constructor).
  useEffect(() => {
    actions.fetchUserCustomers()
      .then((customers: any) => {
        setTargetCustomers(customers.payload.map((customer: any) => ({
          value: customer.id,
          label: customer.name,
        })));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Was componentDidUpdate — runs after every render. The qrcodeurl dispatch is
  // self-limiting: the EDIT_NODE reducer returns the SAME state ref when the value
  // is unchanged (qrcodeurl is a string, so === holds), so useSelector doesn't
  // re-render and it settles after one extra pass. (Immutable used to give this
  // for free; Phase 4's plain reducer keeps it via the explicit equality guard.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const currentNodeId = selectedNode._id;
    if (prevNodeIdRef.current !== currentNodeId) {
      setIsDeleted(false);
      prevNodeIdRef.current = currentNodeId;
    }

    if (typeof typePath === "string") {
      // Ensure QR-cide URL update
      actions.editNode("qrcodeurl", JSON.stringify(typePath));
    };
  });

  const onChangeTargetCustomerName = (e: any) => {
    actions.editNode('customerName', e.label);
    actions.editNode('customerId', e.value);

  }

  const onChangeName = (event: InputEvent) => {
    actions.editNode('name', event.target.value);
  }

  const onChangeTags = (event: InputEvent) => {
    setTagsText(event.target.value);
    // The raw text becomes an array of quote-wrapped tags; it used to reuse one
    // variable for both, which TypeScript rightly rejects.
    const tagsVal = event.target.value.split('","');
    for (let i = 0, ilen = tagsVal.length; i < ilen; i++) {
      tagsVal[i] = tagsVal[i].trim(); // 1st remove start and end whitespaces
      tagsVal[i] = tagsVal[i].replace(/["]/g, ''); // 2nd remove quatation marks
      tagsVal[i] = tagsVal[i].replace(/[']/g, '\u2019');
      tagsVal[i] = tagsVal[i].replace(/[']/g, '');
      tagsVal[i] = '"' + tagsVal[i] + '"' // 3rd add quatation marks to start and end
    };
    updateCExtras({ c84: tagsVal });
  }

  const questionLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 0);
  }
  const feedbackPlusLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 1);
  }
  const feedbackMinusLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 2);
  }
  const feedbackNeutralLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 5);
  }
  const feedbackPlusChanged = (event: InputEvent) => {
    changeDefaultLanguageItem(1, event.target.value);
  }
  const feedbackMinusChanged = (event: InputEvent) => {
    changeDefaultLanguageItem(2, event.target.value);
  }
  const feedbackNeutralChanged = (event: InputEvent) => {
    changeDefaultLanguageItem(5, event.target.value);
  }

  const contactTitleChanged = (event: InputEvent) => {
    changeDefaultLanguageItem(3, event.target.value);
  }

  const changeDefaultLanguageItem = (index: number, value: string) => {
    // deep-copy: selectedNode.question is now the live plain state object (no more
    // .toJS() copy), so mutating it in place would mutate the redux store.
    const questionObj = _.cloneDeep(selectedNode.question) ?? {};
    questionObj['en'][index] = value;
    actions.editNode('question', questionObj);
  }
  //--------------------------------------------------------------------
  // Handle optional fields
  //--------------------------------------------------------------------
  const contactExtraLocalizationChanged = (event: TextAreaEvent) => {
    // Get field and edited language.
    const redisid = event.target.getAttribute('data-redisid');
    const language = event.target.getAttribute('data-language');
    // Get current target extras.
    const extra = selectedNode.extra ?? {};

    // extra.k is a plain array of { id, label:{...} } contact fields.
    const kArray = extra.k || [];

    // Get edited field + its index.
    const fieldId = kArray.findIndex((item: any) => item && item.id === redisid);
    const field = fieldId >= 0 ? kArray[fieldId] : undefined;

    // `language` is guarded alongside the field: getAttribute returns
    // string | null, and it is used as a computed key below.
    if (!field || !language) {
      console.warn('Field not found:[%O]:[%O]', language, redisid);
      return;
    }
    const updatedValue = event.target.value; // .trim();

    // Update field labels (fresh objects/arrays — never mutate state).
    const updatedField = { ...field, label: { ...field.label, [language]: updatedValue } };

    // Update contact extra field.
    const updatedExtra = { ...extra, k: kArray.map((item: any, i: number) => (i === fieldId ? updatedField : item)) };


    // Update whole extra object.
    actions.editNode('extra', updatedExtra);
  }

  const contactTitleLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 3);
  }
  const thankyouChanged = (event: InputEvent) => {
    changeDefaultLanguageItem(4, event.target.value);

  }
  const thankyouLocalizationChanged = (event: TextAreaEvent) => {
    changeLocalization(event, 4);
  }

  const isBlank = (str: any) => {
    return !(_.isString(str) ? !!_.trim(str) : _.isEmpty(str));
  }
  // NOTE: three functions lived here — buildQuestionLocalizations,
  // getQuestionLocalizations and isQuestionLocalisationOk — forming a chain that
  // reported whether a question index had a translation in a given language.
  // The middle one referenced an undefined `questionObj` (only ever a local in
  // changeLocalization below), so calling it would have thrown a ReferenceError.
  // Nothing called isQuestionLocalisationOk, so the whole chain was unreachable
  // and the fault never surfaced — it was the long-standing "'questionObj' is
  // not defined" eslint warning. Deleted; recoverable from git if the feature is
  // ever wanted.
  //--------------------------------------------------------------------
  // Change to localization in the given index. Indexes map to different strings in 'question' object
  //--------------------------------------------------------------------
  const changeLocalization = (event: TextAreaEvent, index: number) => {
    let newValue = '';
    if (isBlank(event.target.value)) {
      event.target.value = ''; // contains only whitespaces, thus cleanup.
    } else {
      newValue = event.target.value;
    };
    // `changedLoc` was built here and never read — removed.
    const loc = event.target.getAttribute('data-redisid');
    if (!loc) {
      return;
    }
    // deep-copy: selectedNode.question is the live plain state object now.
    const questionObj: Record<string, string[]> = _.cloneDeep(selectedNode.question) ?? {};
    if (_.has(questionObj, loc)) {
      questionObj[loc][index] = newValue;
    } else {
      questionObj[loc] = new Array(6); // Size of config.js/QUESTIONS[{en:[<questions>],fi:[<questions>]},..]
      questionObj[loc][index] = newValue;
    }
    let empty = true;
    for (let i = 0; i < 6; i++) { // *** TODO - fix magic numbers
      if (questionObj[loc][i] === null) {
        questionObj[loc][i] = ''; // safe value if empty
      };
      if (questionObj[loc][i] !== '' && typeof questionObj[loc][i] !== 'undefined') {
        empty = false;
      };
    };
    if (empty) {
      console.warn('Localisations for [%O] are missing.', loc);
      delete questionObj[loc];
    };
    actions.editNode('question', questionObj);
  }


  const onChangeExtra = (event: InputEvent) => {
    // `extra` used to name both the k array and the wrapped extra object in turn;
    // they are separate variables now.
    const kArray = selectedNode.extra?.k || [];
    const redisid = event.target.getAttribute('data-redisid') ?? '';
    const existing = _.find(kArray, (val: any) => (_.has(val, 'id') ? val.id === redisid : false));

    let nextExtra: TargetExtra;
    if (_.isUndefined(existing)) {
      // All 37 registered fields are checkboxes, so the old `type === 'text'`
      // branch (labelling from the input's value) was dead and is gone.
      const field = CONTACT_FIELDS_BY_ID[redisid] || {};
      const cachedLocalizations = CACHED_LOCALIZATIONS[redisid] || {};

      const element = {
        id: redisid,
        format: field.format || 'text',
        // `val`, not `value`: onSaveTarget rebuilds the array in this shape and
        // it is the only key that reaches Mongo. The two now agree.
        val: field.redisvalue || '',
        label: assign({}, { en: field.redislabel || redisid }, cachedLocalizations)
      };

      nextExtra = { ...selectedNode.extra, k: [...kArray, element] };
    } else {
      const removedField = kArray.find((i: any) => i && i.id === redisid);
      if (removedField && removedField.label) {
        CACHED_LOCALIZATIONS[redisid] = removedField.label;
      }
      nextExtra = { ...selectedNode.extra, k: kArray.filter((item1: any) => item1.id !== redisid) };
    }

    actions.editNode('extra', nextExtra);
  }

  const onChangeSubtitle = (event: InputEvent) => {
    updateCExtras({ c82: event.target.value });
  }

  const onChangeDescription = (event: InputEvent) => {
    updateCExtras({ c83: event.target.value });
  }

  //--------------------------------------------------------------------
  // ThemeSelector
  const onChangeTheme = (theme: any) => {
    const newThemeId = theme.value;
    const themeIndex = findIndex(THEMES, { value: newThemeId });
    const themeDefaults = THEMES[themeIndex];

    setVotingScale(themeDefaults.votingScale);
    setMinValue(themeDefaults.minValue);
    setMaxValue(themeDefaults.maxValue);
    setIconPrefix(themeDefaults.iconPrefix);

    actions.editNode('theme', theme);
    updateCExtras({
      c42: newThemeId,
      c5: themeDefaults.iconPrefix,
      c6: themeDefaults.votingScale,
      minValue: themeDefaults.minValue,
      maxValue: themeDefaults.maxValue,
      // The measurement type follows the theme, but only when the theme has a
      // matching row — otherwise it keeps whatever it had.
      ...(MEASUREMENT_TYPES.some(x => x.value === newThemeId) ? { measurementTypeId: newThemeId } : {})
    });
  }

  const onChangeMeasurementType = (measurementType: any) => {
    updateCExtras({ measurementTypeId: measurementType.value });
  }

  const onChangeCategory = (category: any) => {
    updateCExtras({ categoryId: category.value });
  }

  const onChangeIconPrefix = (event: InputEvent) => {
    setIconPrefix(event.target.value);
    updateCExtras({ c5: event.target.value });
  }

  const onChangeIconsOrder = () => {
    updateCExtras({ c7: iconsOrder == '<' ? '>' : '<' });
  }

  // The `max - min + 1` arithmetic coerces the DOM's strings to numbers exactly
  // as it did off the self bag — kept verbatim.
  const onChangeMinValue = (event: InputEvent) => {
    const newMin = event.target.value;
    const newScale = Number(maxValue) - Number(newMin) + 1;
    setMinValue(newMin);
    setVotingScale(newScale);
    updateCExtras({ minValue: newMin, c6: newScale });
  }

  const onChangeMaxValue = (event: InputEvent) => {
    const newMax = event.target.value;
    const newScale = Number(newMax) - Number(minValue) + 1;
    setMaxValue(newMax);
    setVotingScale(newScale);
    updateCExtras({ maxValue: newMax, c6: newScale });
  }


  const setCampainNameTitleColor = (title: string, color: string) => {
    const element = document.getElementById('campain-name-label');
    if (element) {
      if (color) {
        element.style.color = color;
      };
      if (title) {
        element.innerText = title;
      };
    };
  };

  const onChangeCampain = (event: InputEvent) => {
    const enabled = event.target.checked;
    if (!enabled) {
      setCampainNameTitleColor('Voting campain disabled', 'grey');
    } else {
      setCampainNameTitleColor('Voting campain name', 'black');
    }
    updateCExtras({ c21: enabled });
  }

  const onChangeCampainTtl = (event: InputEvent) => {
    updateCExtras({ c23: event.target.value });
  }

  const onChangeCampainName = (event: InputEvent) => {
    let name = event.target.value;
    if (isBlank(name) || !selectedNode.extra?.c?.c21) {
      name = '';
      setCampainNameTitleColor('Voting campain disabled', 'grey');
    } else {
      name = name.replace(/\s+/g, ''); // remove whitespaces
      if (name.length >= 2) {
        if (name.length <= 16) {
          setCampainNameTitleColor('Voting campain name', 'black');
        } else {
          setCampainNameTitleColor('Valid election campain name must be between 2-16 alphanumeric chars without spaces', 'red');
        };
      } else {
        setCampainNameTitleColor('Valid election campain name needs at least two alphanumeric chars', 'red');
      }
    };
    // `|| false` mirrors the coercion updateCExtras used to apply to
    // self._campainName: an empty name is stored as false, not ''.
    updateCExtras({ c22: name || false });
  }

  const onChangeEnabled = () => {
    updateCExtras({ enabled: !isEnabled });
  }
  // Path
  const selectPrevTarget = (selection: any) => {
    setPickedPrevTargetId(selection.value);
    updateCExtras();
  };

  const goToPrevTarget = (event: React.MouseEvent<HTMLButtonElement>) => {
    const url = (event.target as HTMLButtonElement).value;
    if (url) {
      window.location.href = url;
    };
  };

  const goToNextTarget = (event: React.MouseEvent<HTMLButtonElement>) => {
    const url = (event.target as HTMLButtonElement).value;
    if (url) {
      window.location.href = url;
    };
  };

  const onChangeNext = (selection: any) => {
    // nextTargetId is parsed back out of c70, so writing the link is enough.
    updateCExtras({ c70: 'https://go.taplause.com/' + selection.value });
  }

  // baseTarget is derived from c67, so setting the id is the whole operation.
  const onChangeSetTarget = (event: InputEvent) => {
    updateCExtras({
      c67: event.target.checked ? TARGET_URL.replace('https://', '') + '/' + selectedNode._id : ''
    });
  }

  const onChangeLinkingTarget = (event: InputEvent) => {
    updateCExtras({ c70: event.target.value });
  }

  const onChangeLandingPage = (event: InputEvent) => {
    updateCExtras({ c99: event.target.value });
  }

  // The CSS input had `onChange={self.onChangeCSS}` — a handler that never
  // existed, so it was `undefined` and typing only ever landed in the DOM, to be
  // picked up by whichever updateCExtras/save ran next. Now it's a real handler.
  const onChangeCSS = (event: InputEvent) => {
    updateCExtras({ css: event.target.value });
  }
  //----------------------------------------------------------
  // Optional question must be selected if details buttons are enabled
  //----------------------------------------------------------
  const onChangeOptionalQuestions = (event: InputEvent) => {
    // Defensive: the checkbox is hidden while detail buttons are on, but if it
    // is reached the answer is forced to true, as before.
    updateCExtras({ c45: showDetailButtons ? true : event.target.checked });
  }
  //----------------------------------------------------------
  // If detail buttons are active optiona question must be enabled
  //----------------------------------------------------------
  const onChangeShowTopicButton = (event: InputEvent) => {
    const enabled = event.target.checked;
    updateCExtras(enabled ? { c78: true, c45: true } : { c78: false });
  }

  //----------------------------------------------------------
  const onChangeGetback = (event: InputEvent) => {
    updateCExtras({ c66: event.target.checked === true });
  };
  //----------------------------------------------------------
  // Disabling linking clears everything that derives from it: the base-target
  // id (and with it baseTarget), getback, and the c70 link — which is also what
  // makes the derived linkingEnabled read false, since it ORs c68 with c70.
  const onChangeEnableLinking = (event: InputEvent) => {
    if (!event.target.checked) {
      updateCExtras({ c68: false, c67: '', c66: false, c70: '' });
      return;
    };
    updateCExtras({ c68: true });
  }
  //----------------------------------------------------------
  const onChangeOptionalFields = (event: InputEvent) => {
    updateCExtras({ c50: event.target.checked });
  }

  const onChangeOptionalThanks = (event: InputEvent) => {
    updateCExtras({ c49: event.target.checked });
  }

  // Seven superuser toggles (c46/c63/c86/c87/c88/c79/gps) each used to have their
  // own one-line handler writing a single c-key. <SuperuserSettingsPanel> drives them
  // from a table, so they share this one instead. Phase 5.7 had already reduced
  // them to one-liners by removing the self-bag consistency assertions; this
  // removes the last of the duplication.
  const onToggleSetting = (cKey: string, checked: boolean) => {
    updateCExtras({ [cKey]: checked });
  }
  //--------------------------------------------------------------------
  // Image button enable/disable
  const onChangeImageButton = (event: InputEvent) => {
    updateCExtras({ c47: event.target.checked });
  }
  //--------------------------------------------------------------------
  // Skip button enable/disable
  const onChangeSkipButton = (event: InputEvent) => {
    updateCExtras({ c48: event.target.checked });
  }
  //--------------------------------------------------------------------
  // Language menu enable/disable
  const onChangeShowLanguageMenu = (event: InputEvent) => {
    updateCExtras({ c9: event.target.checked });
  }
  //--------------------------------------------------------------------
  const onChangeLegalNotice = (event: InputEvent) => {
    updateCExtras({ c80: event.target.checked });
  }
  //--------------------------------------------------------------------
  const onChangeLegalURL = (event: InputEvent) => {
    updateCExtras({ c81: event.target.value });
  }
  //--------------------------------------------------------------------
  const onChangeLotteryRules = (event: InputEvent) => {
    updateCExtras({ c60: event.target.checked });
  }
  //--------------------------------------------------------------------
  const onChangeLotteryURL = (event: InputEvent) => {
    updateCExtras({ c61: event.target.value });
  }
  //--------------------------------------------------------------------
  const setQRcodeButtonsColor = (color: string) => {
    const buttonWithTexts = document.getElementById('qrcode-button');
    if (buttonWithTexts) {
      buttonWithTexts.style.background = color;
    }

    const buttonNoTexts = document.getElementById('qrcode-button-no-texts');
    if (buttonNoTexts) {
      buttonNoTexts.style.background = color;
    }
  }
  //--------------------------------------------------------------------
  // Title/URL are passed in rather than read from state: the callers below run in
  // the same tick as their setQrcodeX call, so the state variables still hold the
  // previous value at that point.
  const isValidQRcodeInput = (title: string, url: string) => {
    if (!isBlank(title) && !isBlank(url)) {
      return true;
    } else {
      return false;
    };
  }
  //--------------------------------------------------------------------
  // Set only the color according to valid input
  const handleQRcodeButton = (title: string, url: string) => {
    if (isValidQRcodeInput(title, url)) {
      setQRcodeButtonsColor('rgba(76, 169, 47, 1)'); // @color-main-green
    } else {
      setQRcodeButtonsColor('grey');
    };
  }
  //--------------------------------------------------------------------
  // QR-code title
  const onChangeQRcodeTitle = (event: InputEvent) => {
    let title = event.target.value;
    if (isBlank(title) || !title) {
      title = '';
      console.warn('*** QR-code title missing:[%O]', title);
    };
    setQrcodeTitle(title);
    handleQRcodeButton(title, qrcodeURL);
    actions.editNode('qrcodetitle', title);
    updateCExtras({ c71: title });
  }
  //--------------------------------------------------------------------
  // QR-code URL
  const onChangeQRcodeURL = (event: InputEvent) => {
    let url = event.target.value;
    if (isBlank(url) || !url) {
      url = '';
    } else { // https/http is hidden
      url = url.replace(/^https:\/\//, '');
      url = url.replace(/^http:\/\//, '');
    };
    setQrcodeURL(url);
    handleQRcodeButton(qrcodeTitle, url);
    actions.editNode('qrcodeurl', url);
    updateCExtras({ c72: url });
  }
  //--------------------------------------------------------------------
  // QR-code (scale) selector
  // - get the selector id and then set the scale
  const onChangeQRcode = (id: any) => {
    // Guard on absence, not falsiness: XXL's id is 0, so the old `if (!newId)`
    // rewrote every XXL pick to 2 ('L'). It went unnoticed while QRcodeSelector
    // displayed 'L' for everything regardless; fixing that display exposed it.
    let newId = id.value;
    if (typeof newId === 'undefined' || newId === null) {
      newId = 2;
    };
    const newScale = QRCODE_SCALE[newId].scale;
    setQrcodeId(newId);
    setQrcodeScale(newScale);
    handleQRcodeButton(qrcodeTitle, qrcodeURL);
    actions.editNode('qrcodeid', newId);
    actions.editNode('qrcodescale', newScale);
    updateCExtras({ c73: newScale, c74: newId });
  }

  const onChangeParent = (parentId: any) => {
    actions.changeParent(parentId.value);
  }

  const onHandleDeleteClick = () => {
    setShowDeleteDialog(true);
  }

  const onHandleConfirm = () => {
    if (isEnabled) {
      return;
    }

    const targetId = selectedNode._id || false;
    actions.deleteTarget(targetId, params.targetId)
      .then((response: any) => {
        if (response) {
          setIsDeleted(true);
        }
      })
    setShowDeleteDialog(false);
  }

  const onHandleCancel = () => {
    setShowDeleteDialog(false);
  }


  const onHandleDeleteFeedbackClick = () => {
    setShowDeleteFeedbackDialog(true);
  }

  const onHandleConfirmDeleteFeedback = () => {
    if (isEnabled) {
      return;
    }

    const targetId = selectedNode._id || false;
    actions.deleteTargetFeedback(targetId, params.targetId)
      .then((response: any) => {
      })
    setShowDeleteFeedbackDialog(false);
  }

  const onHandleCancelDeleteFeedback = () => {
    setShowDeleteFeedbackDialog(false);
  }

  // `overrides` carries the new value of the field whose handler is calling us.
  // The five text fields below (c70/c82/c83/c99/css) used to be read straight off
  // their DOM nodes here; now they're controlled off the store, so the default is
  // to pass the stored value through and let the changing field override it — the
  // store hasn't been updated yet at the moment the handler runs.
  const updateCExtras = (overrides = {}) => {
    let extras = selectedNode.extra;
    extras = { ...extras, c: {
      "c5": iconPrefix,
      "c6": votingScale,
      "c7": iconsOrder,
      "c8": timeoutLang,
      "c9": !!selectedNode.extra?.c?.c9,
      // "c9": selectedNode.extra?.c?.c9, // self._languageMenu,
      "c21": !!selectedNode.extra?.c?.c21,
      "c22": selectedNode.extra?.c?.c22 || false,
      "c23": selectedNode.extra?.c?.c23 ?? false, //  Time to live
      "c42": themeId,
      "c45": optionalQuestions,
      "c46": !!selectedNode.extra?.c?.c46,
      "c47": !!selectedNode.extra?.c?.c47,
      "c48": skipButton,
      "c49": optionalThanks,
      "c50": optionalFields,
      "c60": lotteryRules,
      "c61": selectedNode.extra?.c?.c61 || '',
      "c62": targetType,
      "c63": !!selectedNode.extra?.c?.c63,
      "c66": getback,
      "c67": setTargetId,
      "c68": linkingEnabled,
      // "c69": self._nextTargetId,
      "c70": selectedNode.extra?.c?.c70 || '',
      "c71": qrcodeTitle,
      "c72": qrcodeURL,
      "c73": qrcodeScale,
      "c74": qrcodeId,
      "c78": showDetailButtons,
      "c79": !!selectedNode.extra?.c?.c79,
      "c80": legalNotice,
      "c81": selectedNode.extra?.c?.c81 || '',
      "c82": selectedNode.extra?.c?.c82 || '',
      "c83": selectedNode.extra?.c?.c83 || '',
      "c84": selectedNode.extra?.c?.c84,
      "c86": !!selectedNode.extra?.c?.c86,
      "c87": !!selectedNode.extra?.c?.c87,
      "c88": !!selectedNode.extra?.c?.c88,
      "c99": selectedNode.extra?.c?.c99 || '',
      "enabled": isEnabled,
      "css": selectedNode.extra?.c?.css || '',
      "gps": !!selectedNode.extra?.c?.gps,
      "measurementTypeId": measurementTypeId,
      "categoryId": categoryId,
      "minValue": minValue,
      "maxValue": maxValue,
      ...overrides
    } };
    actions.editNode('extra', extras);
  }

  const onChangeQuestion = (questionId: any) => {
    if (_.isString(questionId)) {
      let questionObj = { en: new Array(6) }; // question,pos,neg,contact,thanks,neutral
      questionObj.en.fill("");
      questionObj.en[0] = questionId;
      actions.editNode('question', questionObj);
    } else {
      // QUESTIONS rows are typed { en, fi } from the config literal, but a target
      // may lead with any language, so index it as an open map.
      const question: Record<string, string[]> = QUESTIONS[questionId.value];
      const defaultLanguage = Object.keys(selectedNode.question ?? {})[0];
      // Keep the target's current lead language first — but ONLY if the chosen
      // default question actually has that language. The built-in QUESTIONS only
      // carry en/fi, so a target that leads with any other language (e.g. an
      // added `sv` localization) would otherwise produce `{ [lang]: undefined }`,
      // which crashes the localization panels (they read v[index] on every value).
      const hasLeadLanguage = Object.prototype.hasOwnProperty.call(question, defaultLanguage);
      // Plain object; property insertion order keeps the lead language first
      // (mirrors the old Immutable OrderedMap ordering).
      const newQuestion = hasLeadLanguage
        ? { [defaultLanguage]: question[defaultLanguage], ...omit(question, [defaultLanguage]) }
        : { ...question };

      actions.editNode('question', newQuestion);
    }
  }
  //-------------------------------------------------------
  // Default images selection
  //-------------------------------------------------------
  const onChangePicture = (picture: any) => {
    const pictureId = picture.value;
    if (typeof pictureId !== 'undefined' && pictureId >= 0) {
      actions.editNode('customImagePreview', null);
    };
    const pictures = PICTURES.concat(selectedNode.customizations?.picture || []);
    const index = pictures.indexOf(picture.label);
    if (index >= 0) {
      actions.editNode('picture', pictures[index]);
    } else {
      console.error('Image handling error.');
    };
  }

  const onFileUpload = (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }

    // react-dropzone v7+ no longer auto-adds file.preview; create it ourselves.
    const preview = (file as any).preview || URL.createObjectURL(file);
    actions.editNode('customImagePreview', preview);
    actions.editNode('customImage', file);
  }

  // Rebuild extra.k from the store rather than from the DOM: the ticked fields
  // are exactly those whose redisid is in extra.k, and the rest of each entry
  // comes from CONTACT_FIELDS. Existing labels win so localizations survive.
  const buildContactFormFields = () => CONTACT_FIELDS
    .filter(field => extraKIds.has(field.redisid))
    .map(field => {
      const found = (selectedNode.extra?.k || []).find((item: any) => item && item.id === field.redisid);
      return {
        id: field.redisid,
        label: (found && found.label) ? found.label : { en: field.redislabel || '' },
        format: field.format || 'text',
        val: field.redisvalue || ''
      };
    });
  //----------------------------------------------------------------
  // Save button handler
  //----------------------------------------------------------------
  const onSaveTarget = (preview = false) => {
    ;
    const contactFormShowFields = buildContactFormFields();
    let newExtras = selectedNode.extra;
    if (contactFormShowFields.length > 0) {
      newExtras = { ...selectedNode.extra, k: contactFormShowFields };
    }
    newExtras = { ...newExtras, c: {
      "c5": iconPrefix,
      "c6": votingScale,
      "c7": iconsOrder,
      "c8": timeoutLang,
      "c9": !!selectedNode.extra?.c?.c9,
      // "c9": selectedNode.extra?.c?.c9, // self._languageMenu,
      "c21": !!selectedNode.extra?.c?.c21, // true/false
      "c22": selectedNode.extra?.c?.c22 || false,
      "c23": selectedNode.extra?.c?.c23 ?? false, //  Time to live
      "c42": themeId,
      "c45": optionalQuestions,
      "c46": !!selectedNode.extra?.c?.c46,
      "c47": !!selectedNode.extra?.c?.c47,
      "c48": skipButton,
      "c49": optionalThanks,
      "c50": optionalFields,
      "c60": lotteryRules,
      "c61": selectedNode.extra?.c?.c61 || '',
      "c62": targetType,
      "c63": !!selectedNode.extra?.c?.c63,
      "c66": getback,
      "c67": setTargetId,
      "c68": linkingEnabled,
      // "c69": self._nextTargetId,
      "c70": selectedNode.extra?.c?.c70 || '',
      "c71": qrcodeTitle,
      "c72": qrcodeURL,
      "c73": qrcodeScale,
      "c74": qrcodeId,
      "c78": showDetailButtons,
      "c79": !!selectedNode.extra?.c?.c79,
      "c80": legalNotice,
      "c81": selectedNode.extra?.c?.c81 || '',
      "c82": selectedNode.extra?.c?.c82 || '',
      "c83": selectedNode.extra?.c?.c83 || '',  // Remember: Structured variable
      "c84": selectedNode.extra?.c?.c84,
      "c86": !!selectedNode.extra?.c?.c86,
      "c87": !!selectedNode.extra?.c?.c87,
      "c88": !!selectedNode.extra?.c?.c88,
      "c99": selectedNode.extra?.c?.c99 || '',
      "enabled": isEnabled,
      "css": selectedNode.extra?.c?.css || '',
      "gps": !!selectedNode.extra?.c?.gps,
      "measurementTypeId": measurementTypeId,
      "categoryId": categoryId,
      "minValue": minValue,
      "maxValue": maxValue
    } };
    actions.editNode('extra', newExtras);

    let node: TargetNode = { ...selectedNode, extra: newExtras };

    if (selectedNode.customImagePreview) {
      // Guarded together: customImagePreview is only ever set alongside the File.
      const picture = selectedNode.customImage;
      if (!picture) {
        return;
      }
      actions.signS3Upload(picture.name, picture.type)
        .then((response: any) => {
          return actions.s3Upload(picture, response.payload);
        })
        .then((response: any) => {
          const pictureUrl = response.payload;

          actions.editNode('customImagePreview', null);
          actions.editNode('customImage', null);
          actions.editNode('picture', pictureUrl);
          node = {
            ...selectedNode,
            customImagePreview: null,
            customImage: null,
            picture: pictureUrl
          };
          actions.setCustomPicture(pictureUrl);
          return actions.saveTarget(node, params.targetId).then(() => {
            // Open modified target in a new tab after save
            if (preview === true) {
              window.open(TARGET_URL + "/" + selectedNode._id, '_blank');
            }
          });
        });
    } else {
      actions.saveTarget(node, params.targetId)
        .then(() => {
          // Open modified target in a new tab after save
          if (preview === true) {
            window.open(TARGET_URL + "/" + selectedNode._id, '_blank');
          }
        });
    }
  }


  //----------------------------------------------------------------
  // Clone target with new name without QR-code items
  //----------------------------------------------------------------
  const onCloneTarget = () => {
    const omitExtraC = ['c71', 'c72', 'c73', 'c74'];
    const cloneName = 'Clone of ' + selectedNode.name;
    const sourceTarget = { ...selectedNode, name: cloneName };
    actions.addTarget(sourceTarget, params.targetId, omitExtraC);
  }
  //----------------------------------------------------------------
  const onChangeType = (e: any) => {
    const index = e.value;
    const newTypePath = TARGET_URL + '/' + TARGET_TYPES[index].path + '/' + selectedNode._id;
    setQrcodeURL(newTypePath);
    updateCExtras({ c62: index, c72: newTypePath });
  }
  //----------------------------------------------------------------
  const targetIdToClipboard = (e: any) => {
    const value = selectedNode._id;
    if (!value) {
      return;
    }
    navigator.clipboard.writeText(value);
    const element = document.getElementById('target-id');
    if (!element) {
      return;
    }
    element.innerHTML = ' ' + t('clipboard.copied');
    setTimeout(function () {
      element.innerHTML = ''
    }, 5000);
  }

  return render();
};

export default withTranslation('common')(SelectedNode);
